/** Stripe webhook shapes (docs/53 §6.3). See ./index.ts for the verification rule and the VERIFY note. */
import { z } from 'zod';

export const STRIPE_SIGNATURE_HEADER = 'Stripe-Signature';
export const STRIPE_TOLERANCE_SECONDS = 300;

const StripeId = (prefix: string) => z.string().regex(new RegExp(`^${prefix}_[A-Za-z0-9]+$`));

const StripeEventBase = z.object({
  id: StripeId('evt'),
  object: z.literal('event'),
  created: z.number().int().positive(),
  livemode: z.boolean(),
  api_version: z.string().nullable().optional(),
});

export const StripeCheckoutCompleted = StripeEventBase.extend({
  type: z.literal('checkout.session.completed'),
  data: z.object({
    object: z.looseObject({
      // Checkout session ids carry the mode: cs_test_... and cs_live_...
      id: z.string().regex(/^cs_(test_|live_)?[A-Za-z0-9]+$/),
      mode: z.enum(['payment', 'setup', 'subscription']),
      customer: StripeId('cus').nullable(),
      payment_status: z.enum(['paid', 'unpaid', 'no_payment_required']),
      amount_total: z.number().int().nullable(),
      currency: z.string().length(3).nullable(),
      payment_intent: StripeId('pi').nullable().optional(),
      setup_intent: StripeId('seti').nullable().optional(),
      client_reference_id: z.string().max(200).nullable().optional(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
  }),
});

const InvoiceObject = z.looseObject({
  id: StripeId('in'),
  customer: StripeId('cus'),
  amount_due: z.number().int(),
  amount_paid: z.number().int(),
  attempt_count: z.number().int().min(0),
  next_payment_attempt: z.number().int().nullable().optional(),
  status: z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']).nullable(),
  hosted_invoice_url: z.string().nullable().optional(),
});

export const StripeInvoicePaid = StripeEventBase.extend({ type: z.literal('invoice.paid'), data: z.object({ object: InvoiceObject }) });
export const StripeInvoicePaymentFailed = StripeEventBase.extend({ type: z.literal('invoice.payment_failed'), data: z.object({ object: InvoiceObject }) });

const SubscriptionObject = z.looseObject({
  id: StripeId('sub'),
  customer: StripeId('cus'),
  status: z.enum(['incomplete', 'incomplete_expired', 'trialing', 'active', 'past_due', 'canceled', 'unpaid', 'paused']),
  cancel_at: z.number().int().nullable(),
  canceled_at: z.number().int().nullable().optional(),
  metadata: z.record(z.string(), z.string()).nullable().optional(),
});

export const StripeSubscriptionEvent = StripeEventBase.extend({
  type: z.enum(['customer.subscription.created', 'customer.subscription.updated', 'customer.subscription.deleted']),
  data: z.object({ object: SubscriptionObject, previous_attributes: z.record(z.string(), z.unknown()).optional() }),
});

export const StripeChargeRefunded = StripeEventBase.extend({
  type: z.literal('charge.refunded'),
  data: z.object({ object: z.looseObject({ id: StripeId('ch'), customer: StripeId('cus').nullable(), amount_refunded: z.number().int() }) }),
});

/** Events we act on. Anything else is recorded in `stripe_events` and ignored. */
export const StripeHandledEvent = z.discriminatedUnion('type', [
  StripeCheckoutCompleted,
  StripeInvoicePaid,
  StripeInvoicePaymentFailed,
  StripeSubscriptionEvent,
  StripeChargeRefunded,
]);
export const StripeAnyEvent = StripeEventBase.extend({ type: z.string().max(100), data: z.object({ object: z.looseObject({ id: z.string() }) }) });
